import type { InitialState } from '@/app';
import {
  BRANCH_DAILY_SALES_COMPARE_REPORT_ID,
  BRANCH_DAILY_SALES_REPORT_ID,
  HOURLY_SALES_REPORT_ID,
  BRANCH_MONTHLY_SALES_REPORT_ID,
  DAILY_SALES_DASHBOARD_MODULE_ID,
  INVENTORY_DASHBOARD_MODULE_ID,
  DO_DETAILS_REPORT_ID,
  GRN_DETAILS_REPORT_ID,
  PO_DETAILS_REPORT_ID,
  SALES_BY_SKU_REPORT_ID,
  SALES_BY_PROMOTION_REPORT_ID,
  COUPON_USAGE_REPORT_ID,
  VOUCHER_USAGE_REPORT_ID,
  BRANCH_STOCK_BALANCE_REPORT_ID,
  STOCK_INVENTORY_REPORT_ID,
  STOCK_AGING_REPORT_ID,
  STOCK_MOVEMENT_REPORT_ID,
  YEARLY_SALES_COMPARISON_REPORT_ID,
  FISCAL_WEEKLY_SALES_REPORT_ID,
  BRANCH_FINANCIAL_PERFORMANCE_REPORT_ID,
  PROMOTION_PERFORMANCE_DASHBOARD_REPORT_ID,
  WEEKLY_COMPANY_REPORT_MODULE_ID,
  SALES_SUMMARY_MODULE_ID,
} from '@/config/portalMenu';

export default function access(initialState: InitialState) {
  const role = initialState?.currentUser?.role;
  const isAdmin = role === 'admin';
  const userReports = initialState?.userReports ?? [];
  const userModules = initialState?.userModules ?? [];
  const canViewBranchDailySales =
    isAdmin || userReports.includes(BRANCH_DAILY_SALES_REPORT_ID);
  const canViewHourlySales =
    isAdmin || userReports.includes(HOURLY_SALES_REPORT_ID);
  const canViewBranchDailySalesCompare =
    isAdmin || userReports.includes(BRANCH_DAILY_SALES_COMPARE_REPORT_ID);
  const canViewBranchMonthlySales =
    isAdmin || userReports.includes(BRANCH_MONTHLY_SALES_REPORT_ID);
  const canViewYearlySalesComparison =
    isAdmin || userReports.includes(YEARLY_SALES_COMPARISON_REPORT_ID);
  const canViewFiscalWeeklySales =
    isAdmin || userReports.includes(FISCAL_WEEKLY_SALES_REPORT_ID);
  const canViewSalesBySku = isAdmin || userReports.includes(SALES_BY_SKU_REPORT_ID);
  const canViewSalesByPromotion =
    isAdmin || userReports.includes(SALES_BY_PROMOTION_REPORT_ID);
  const canViewCouponUsage = isAdmin || userReports.includes(COUPON_USAGE_REPORT_ID);
  const canViewVoucherUsage = isAdmin || userReports.includes(VOUCHER_USAGE_REPORT_ID);
  const canViewPoDetails = isAdmin || userReports.includes(PO_DETAILS_REPORT_ID);
  const canViewGrnDetails = isAdmin || userReports.includes(GRN_DETAILS_REPORT_ID);
  const canViewDoDetails = isAdmin || userReports.includes(DO_DETAILS_REPORT_ID);
  const canViewStockInventory = isAdmin || userReports.includes(STOCK_INVENTORY_REPORT_ID);
  const canViewStockAging = isAdmin || userReports.includes(STOCK_AGING_REPORT_ID);
  const canViewStockMovement = isAdmin || userReports.includes(STOCK_MOVEMENT_REPORT_ID);
  const canViewBranchStockBalance =
    isAdmin || userReports.includes(BRANCH_STOCK_BALANCE_REPORT_ID);
  const canViewCost = isAdmin || userModules.includes('view_cost');
  const canAccessPoChecker = isAdmin || userModules.includes('po_checker');
  const canAccessDoChecker = isAdmin || userModules.includes('do_checker');
  const canAccessGrChecker = isAdmin || userModules.includes('gr_checker');
  const canAccessSkuChecker = isAdmin || userModules.includes('sku_checker');
  const canAccessQohChecker = isAdmin || userModules.includes('qoh_checker');
  const canAccessPriceChecker = isAdmin || userModules.includes('price_checker');
  const canAccessPromotionChecker = isAdmin || userModules.includes('promotion_checker');
  const canViewDailySalesDashboard =
    isAdmin || userModules.includes(DAILY_SALES_DASHBOARD_MODULE_ID);
  const canViewWeeklyCompanyReport =
    isAdmin || userModules.includes(WEEKLY_COMPANY_REPORT_MODULE_ID);
  const canViewSalesSummary =
    isAdmin || userModules.includes(SALES_SUMMARY_MODULE_ID);
  const canViewInventoryDashboard =
    isAdmin || userModules.includes(INVENTORY_DASHBOARD_MODULE_ID);
  const canViewBranchFinancialPerformance =
    isAdmin || userReports.includes(BRANCH_FINANCIAL_PERFORMANCE_REPORT_ID);
  const canViewPromotionPerformanceDashboard =
    isAdmin ||
    userReports.includes(PROMOTION_PERFORMANCE_DASHBOARD_REPORT_ID) ||
    userReports.includes(SALES_BY_SKU_REPORT_ID) ||
    userReports.includes(SALES_BY_PROMOTION_REPORT_ID) ||
    userModules.includes(DAILY_SALES_DASHBOARD_MODULE_ID);
  const canEditPublicHolidays = isAdmin || userModules.includes('public_holidays');
  const canViewMembershipDashboard = isAdmin || userModules.includes("membership_dashboard");
  const canViewProductPerformanceDashboard = isAdmin || userModules.includes('product_performance_dashboard');
  const canViewDashboard =
    canViewProductPerformanceDashboard ||
    canViewMembershipDashboard ||
    canViewDailySalesDashboard ||
    canViewWeeklyCompanyReport ||
    canViewSalesSummary ||
    canViewInventoryDashboard ||
    canViewBranchFinancialPerformance ||
    canViewPromotionPerformanceDashboard;

  return {
    canViewProductPerformanceDashboard,
    canViewMembershipDashboard,
    canAdmin: isAdmin,
    canAccessUserManagement: isAdmin,
    canAccessEmailSettings: isAdmin,
    canAccessBranchAdmin: isAdmin,
    canAccessSyncTools: isAdmin,
    canViewBranchDailySales,
    canViewHourlySales,
    canViewBranchDailySalesCompare,
    canViewBranchMonthlySales,
    canViewYearlySalesComparison,
    canViewFiscalWeeklySales,
    canViewSalesBySku,
    canViewSalesByPromotion,
    canViewCouponUsage,
    canViewVoucherUsage,
    canViewPoDetails,
    canViewGrnDetails,
    canViewDoDetails,
    canViewStockInventory,
    canViewStockAging,
    canViewStockMovement,
    canViewBranchStockBalance,
    canViewCost,
    canAccessPoChecker,
    canAccessDoChecker,
    canAccessGrChecker,
    canAccessSkuChecker,
    canAccessQohChecker,
    canAccessPriceChecker,
    canAccessPromotionChecker,
    canViewDailySalesDashboard,
    canViewWeeklyCompanyReport,
    canViewSalesSummary,
    canViewInventoryDashboard,
    canViewBranchFinancialPerformance,
    canViewPromotionPerformanceDashboard,
    canEditPublicHolidays,
    canViewDashboard,
  };
}
